import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LockClosedIcon, EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { API_URLS } from '../config/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  
  const token = searchParams.get('token');

  // Determine the API URL on component mount
  useEffect(() => {
    const hostname = window.location.hostname;
    let baseUrl;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      baseUrl = API_URLS.default;
    } else {
      baseUrl = API_URLS.default;
    }
    
    console.log('Using API URL:', baseUrl);
    setApiUrl(baseUrl);
  }, []);

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setError('No reset token provided. Please check your email link.');
      setValidatingToken(false);
      return;
    }

    if (!apiUrl) return;

    const validateToken = async () => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/auth/validate-reset-token`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 15000;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.valid) {
                setTokenValid(true);
              } else {
                setError(data.message || 'Invalid or expired reset token');
              }
            } catch (parseError) {
              setError('Invalid response from server');
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              setError(errorData.message || 'Failed to validate reset token');
            } catch (parseError) {
              setError('Invalid or expired reset token');
            }
          }
          setValidatingToken(false);
        };
        
        xhr.onerror = function() {
          setError('Network error. Please check your connection and try again.');
          setValidatingToken(false);
        };
        
        xhr.ontimeout = function() {
          setError('Request timed out. Please try again.');
          setValidatingToken(false);
        };
        
        xhr.send(JSON.stringify({ token }));
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Failed to validate reset token');
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, apiUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/reset-password`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000;
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setMessage(data.message);
            setResetSuccess(true);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            setError('Invalid response from server');
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            setError(errorData.message || 'Failed to reset password');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
        }
        setLoading(false);
      };
      
      xhr.onerror = function() {
        console.error('XHR error:', xhr.statusText);
        setError('Network error. Please check your connection and try again.');
        setLoading(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setLoading(false);
      };
      
      xhr.send(JSON.stringify({
        token,
        password: formData.password
      }));
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
        <div className="container mx-auto px-4 h-full">
          <div className="flex content-center items-center justify-center min-h-screen py-12">
            <div className="w-full lg:w-5/12 px-4">
              <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
                <div className="flex-auto px-8 py-16 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Validating reset token...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center min-h-screen py-12">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                <div className="text-center mb-3">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Reset Password
                  </h1>
                  <p className="text-gray-600 text-sm mb-4">
                    {resetSuccess 
                      ? "Your password has been successfully reset!" 
                      : "Enter your new password below"
                    }
                  </p>
                </div>
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-center">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-center">
                    {message}
                  </div>
                )}

                {!tokenValid && !validatingToken ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                      <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-gray-900">Invalid Reset Link</h3>
                      <p className="text-sm text-gray-600">
                        This password reset link is invalid or has expired.
                      </p>
                      <p className="text-xs text-gray-500">
                        Reset links expire after 1 hour for security.
                      </p>
                    </div>
                    <div className="space-x-4">
                      <Link
                        to="/forgot-password"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        Request New Reset
                      </Link>
                      <Link
                        to="/login"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Back to Login
                      </Link>
                    </div>
                  </div>
                ) : resetSuccess ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                      <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-gray-900">Password Reset Successful!</h3>
                      <p className="text-sm text-gray-600">
                        Your password has been reset successfully. You can now log in with your new password.
                      </p>
                      <p className="text-xs text-gray-500">
                        You will be redirected to the login page in a few seconds...
                      </p>
                    </div>
                    <Link
                      to="/login"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      Go to Login
                    </Link>
                  </div>
                ) : (
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="relative">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockClosedIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          required
                          className="appearance-none block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters long</p>
                    </div>

                    <div className="relative">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockClosedIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          required
                          className="appearance-none block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="text-center mt-6">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Resetting password...
                          </div>
                        ) : (
                          'Reset Password'
                        )}
                      </button>
                    </div>
                  </form>
                )}

                <div className="text-center mt-6">
                  <Link 
                    to="/login" 
                    className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 font-medium"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword; 